import { getStorage, ref, uploadBytes } from "firebase/storage";
import {
  getFirestore,
  getDoc,
  updateDoc,
  doc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  confirmPasswordReset,
} from "firebase/auth";
import {
  cloudFlareImageResize,
  generateID,
  getUserLocationFromIP,
} from "./utils";
import {
  MeasuringCategory,
  ProfileInterface,
  ProfileRole,
} from "ts-interfaces";

const product_cache: any = {};

class FirebaseHelper {
  firebaseApp: any;
  storage: any;
  db: any;
  profilesDB: any;
  productsDB: any;
  suppliersDB: any;
  fulfillersDB: any;
  pendingInvitationsDB: any;
  projectsDB: any;
  projectsSchemeDB: any;
  notificationsDB: any;
  notificationsViewedDB: any;
  userProductsDB: any;
  static instance: any;

  constructor() {
    if (!FirebaseHelper.instance) {
      FirebaseHelper.instance = this;
    }
    // Initialize object
    return FirebaseHelper.instance;
  }

  initializeApp = (firebaseConfig) => {
    if (this.firebaseApp) {
      console.log("Firebaseapp already initialized!");
      return;
    }
    this.firebaseApp = initializeApp(firebaseConfig);
    this.storage = getStorage(this.firebaseApp);
    this.db = getFirestore(this.firebaseApp);
    this.profilesDB = collection(this.db, "profiles");
    this.suppliersDB = collection(this.db, "suppliers");
    this.fulfillersDB = collection(this.db, "fulfillers");
    this.productsDB = collection(this.db, "productsV2");
    this.pendingInvitationsDB = collection(this.db, "pending_invitations");
    this.projectsDB = collection(this.db, "projects");
    this.projectsSchemeDB = collection(this.db, "projectsScheme");
    this.notificationsDB = collection(this.db, "notifications");
    this.notificationsViewedDB = collection(this.db, "notifications_viewed");
    this.userProductsDB = collection(this.db, "userProducts");
  };

  googleSignup = () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });
    provider.addScope("profile");
    provider.addScope("email");
    return signInWithPopup(getAuth(), provider);
  };

  getDocByID = (collection, id) => {
    return getDoc(doc(this.db, collection, id));
  };


    uploadAndSave2 =(collectionRef, recordId:any=null, userId:any=null, data:any={}, files={},fileHeaders={cacheControl:'public,max-age=31536000'}) =>{        
        userId = userId==null ? '' : '/' + userId
        return new Promise((resolve, reject) => {
            if (!collectionRef) reject("No collectionRef")
            const promises=[]         
            for (const key in files) {
                    if (files[key] instanceof Blob == false && files[key] instanceof File ==false) 
                            return reject("All files must be of type Blob or File!");                               
                    if (data.files==null ) data.files={};
                    const ext =  (files[key] as any).extension || ''
                    let filename=`${collectionRef.parent.path}${userId}/${recordId}/${Date.now()}_${key}${ext}`
                    if (filename.includes('//')) { filename = filename.replace('//', '/') }
                    data.files[key] =  filename
                    console.log("putting ", filename, 'and', fileHeaders, ' and ', files[key]);                     
                    const fileRef = ref(this.storage, filename)
                    promises.push( uploadBytes(fileRef, files[key], fileHeaders) )
            }
            promises.unshift(setDoc(collectionRef, data, {merge:true}))
            Promise.all(promises).then(d=> {
                resolve(data)
            }).catch(error=> { console.log("Error saving ", error, userId, recordId); reject(error) } )
        })
    }
    /*
        uploadAndSave is a function that uploads files to Firebase Storage and saves the data to the database.
        This can handle both the creation and the update of a record.
        Requires an associated firebase userID to operate 
    */
    uploadAndSave = (recordId:any = null, collection, userId:any = null, data:any = {}, files = {}, fileHeaders = { cacheControl: 'public,max-age=31536000' }) => {
        userId = userId==null ? '' : '/' + userId
        const collectionRef = doc(this.db, collection, recordId)
        return this.uploadAndSave2(collectionRef,recordId,userId,data,files,fileHeaders)    
    }


  // Add this new function to delete the oldest version
  deleteOldestVersion = async (collectionName, recordId) => {
    try {
      // Get reference to the versions subcollection
      const versionsRef = collection(
        this.db,
        `${collectionName}/${recordId}/versions`
      );

      // Query for all versions ordered by timestamp (ascending so oldest is first)
      const oldestVersionQuery = query(
        versionsRef,
        orderBy("versionTimestamp", "asc"),
        limit(1)
      );

      const querySnapshot = await getDocs(oldestVersionQuery);

      if (!querySnapshot.empty) {
        const oldestVersion = querySnapshot.docs[0];
        const oldestVersionRef = doc(
          this.db,
          `${collectionName}/${recordId}/versions`,
          oldestVersion.id
        );

        await deleteDoc(oldestVersionRef);
        console.log(
          `Deleted oldest version ${oldestVersion.id} for ${collectionName}/${recordId}`
        );
        return true;
      }

      return false;
    } catch (error) {
      console.error(`Error deleting oldest version: ${error}`);
      return false;
    }
  };

  createNewVersion = async (collectionName, recordId, data, files = {}, fileHeaders = { cacheControl: 'public,max-age=31536000' }) => {
    try {
      // Get the current document
      const docRef = doc(this.db, collectionName, recordId);
      const docSnapshot = await getDoc(docRef);

      // Only check for versions if the document already exists
      if (!docSnapshot.exists()) {
        return false;
      }

      // Get the versions subcollection
      const versionsRef = collection(
        this.db,
        `${collectionName}/${recordId}/versions`
      );

      // Query for the latest version
      const latestVersionQuery = query(
        versionsRef,
        orderBy("versionTimestamp", "desc"),
        limit(1)
      );

      const querySnapshot = await getDocs(latestVersionQuery);

      let shouldCreateVersion = true;
      const currentTime = new Date();

      // If there's a previous version, check its timestamp
      if (!querySnapshot.empty) {
        const latestVersionDoc = querySnapshot.docs[0];
        const latestVersion = latestVersionDoc.data() as {
          versionTimestamp: { toDate: () => Date };
        };
        const latestVersionTime = latestVersion.versionTimestamp.toDate();

        // Check if 15 minutes (900000 ms) have passed
        const timeDifference =
          currentTime.getTime() - latestVersionTime.getTime();
        shouldCreateVersion = timeDifference >= 900000; // 15 minutes in milliseconds
      }

      // Create a new version if 15 minutes have passed or if there are no versions yet
      if (shouldCreateVersion) {

        // If we get 10 results, we know we've reached the limit
        const MAX_VERSIONS = 10;
        const countQuery = query(versionsRef, limit(MAX_VERSIONS));
        const countSnapshot = await getDocs(countQuery);

        // If we have 10 versions (max limit), delete the oldest one
        if (countSnapshot.size >= MAX_VERSIONS) {
          await this.deleteOldestVersion(collectionName, recordId);
        }

        const versionId = `${Math.random().toString(36).substring(2, 8)}`;

        // Create a version document in the subcollection
        const versionRef = doc(
          this.db,
          `${collectionName}/${recordId}/versions`,
          versionId
        );

        // Handle file uploads for versions (similar to uploadAndSave2)
        const promises = [];
        const versionData = { ...data };

        // Upload files if any are provided
        for (const key in files) {
          if (files[key] instanceof Blob == false && files[key] instanceof File == false) {
            throw new Error("All files must be of type Blob or File!");
          }
          if (versionData.files == null) versionData.files = {};
          const ext = (files[key] as any).extension || '';
          let filename = `${collectionName}/${recordId}/versions/${versionId}/${Date.now()}_${key}${ext}`;
          versionData.files[key] = filename;
          const fileRef = ref(this.storage, filename);
          promises.push(uploadBytes(fileRef, files[key], fileHeaders));
        }

        // Add version metadata
        versionData.versionTimestamp = currentTime;
        versionData.versionId = versionId;

        // Save version document
        promises.unshift(setDoc(versionRef, versionData));

        // Execute all promises (file uploads + document save)
        await Promise.all(promises);


        return true;
      } else {
        console.log(
          `Skipping version creation (less than 15 minutes since last version)`
        );
        return false;
      }
    } catch (error) {
      console.error(`Error in createNewVersion: ${error}`);
      return false;
    }
  };



  getVersionHistory = async (collectionName, recordId) => {
    const versionsRef = collection(
      this.db,
      `${collectionName}/${recordId}/versions`
    );
    const querySnapshot = await getDocs(versionsRef);

    const versions = [];
    querySnapshot.forEach((doc) => {
      versions.push({
        id: doc.id,
        ...(doc.data() as object),
      });
    });

    // Sort by timestamp, newest first
    return versions.sort((a, b) => b.versionTimestamp - a.versionTimestamp);
  };

  // Add this to firebase.ts class
  getVersionById = async (collectionName, recordId, versionId) => {
    try {
      const versionRef = doc(
        this.db,
        `${collectionName}/${recordId}/versions`,
        versionId
      );
      const versionSnapshot = await getDoc(versionRef);

      if (versionSnapshot.exists()) {
        return versionSnapshot.data();
      } else {
        throw new Error(`Version ${versionId} not found`);
      }
    } catch (error) {
      console.error(`Error retrieving version ${versionId}:`, error);
      throw error;
    }
  };

  deleteRecordAndFiles = (recordId, collection, skipDeletingRecord = false) => {
    const collectionRef = doc(this.db, collection, recordId);
    return new Promise((resolve, reject) => {
      const promises = [];
      getDoc(collectionRef)
        .then((doc) => {
          if (!doc.exists()) return resolve("no record to delete.");
          //for (var key in doc.data().files || {}) promises.push( deleteObject(ref(storage, doc.data().files[key])) )
          if (skipDeletingRecord == false)
            promises.push(deleteDoc(collectionRef));
          Promise.all(promises)
            .then((values) => resolve(values))
            .catch((error) => reject(error));
        })
        .catch((error) => {
          console.log("Error getting data: ", error);
        });
    });
  };

  uploadFileToGCloud = (
    collection,
    id,
    file,
    fileHeaders = { cacheControl: "public,max-age=31536000" }
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!collection || !id) reject("No collection or id");
      if (file instanceof Blob == false && file instanceof File == false)
        reject("All files must be of type blob or file!");
      const extension = file?.name?.split(".")?.pop() ?? "jpg";
      const filename = `${collection}/${id}.${extension}`;
      const fileRef = ref(this.storage, filename);
      uploadBytes(fileRef, file)
        .then((file) => {
          resolve(this.publicURL(file.ref.fullPath));
        })
        .catch((error) => reject(error));
    });
  };

  updateRecord = (recordId, collection, data) => {
    const collectionRef = doc(this.db, collection, recordId);
    return setDoc(collectionRef, data, { merge: true });
  };

  updateFieldsInRecord = (recordId, collection, data) => {
    const collectionRef = doc(this.db, collection, recordId);
    return updateDoc(collectionRef, data);
  };

  sendResetPassword = (email) => {
    return sendPasswordResetEmail(getAuth(), email);
  };
  resetPassword = (oobCode, password) => {
    return confirmPasswordReset(getAuth(), oobCode, password);
  };

  getProductCache = (id, userCreated) => {
    return new Promise((resolve, reject) => {
      if (!id) reject("No id");
      if (product_cache[id]) resolve(product_cache[id]);
      else {
        const collectionName = userCreated ? "userProducts" : "productsV2";
        const docRef = doc(this.db, collectionName, id);
        getDoc(docRef).then((doc) => {
          if (doc.exists()) {
            product_cache[id] = doc.data();
            resolve(doc.data());
          } else reject("No product found! " + id);
        });
      }
    });
  };

  URLFromRef = (file, useCDN = false) => {
    if (!file) return;
    if (file.startsWith("/material_test")) return file;
    if (file.startsWith("http") || file.startsWith("blob")) return file;
    if (file.startsWith("data:") || file.tagName == "IMG") return file;
    if (file.startsWith("gs://")) {
      file = file.replace("gs://mattoboard-b8284.appspot.com/", "");
    }
    file.replaceAll("/", "%2F");
    if (
      useCDN &&
      (file.endsWith(".jpg") ||
        file.endsWith(".webp") ||
        file.endsWith(".jpeg"))
    )
      return cloudFlareImageResize(this.publicURL(file));
    else return this.publicURL(file);
  };

  publicURL(filePath) {
    return (
      "https://storage.googleapis.com/" +
      this.firebaseApp.options.storageBucket +
      "/" +
      filePath
    );
    //console.log(filePath);
    //console.log(this.firebaseApp.options, filePath);
    console.log("We have ", this?.firebaseApp);
    let x = null;
    try {
      x =
        "https://storage.googleapis.com/" +
        this.firebaseApp.options.storageBucket +
        "/" +
        filePath;
    } catch {
      debugger;
      1;
    }
    return x;

    //return `https://firebasestorage.googleapis.com/${this.storage._bucket.bucket}/${filePath}`
  }
}
export const firebaseHelper = new FirebaseHelper();
